
/**
 * React SPA template prompt.
 *
 * The source of truth for this content is:
 *   gui/src/apps/AppBuilderApp/templates/react-spa/PROMPT.md
 *
 * This TypeScript file mirrors it so webpack can bundle it without a raw-loader.
 * Variables {{projectName}}, {{description}}, and {{targetEntityId}} are replaced
 * at runtime by the use_template command handler.
 */

export const REACT_SPA_PROMPT = `
# Template: React Single-Page App

You are building **{{projectName}}** — {{description}}

The project (entityId: \`{{targetEntityId}}\`) is open in AppBuilderApp. Use \`code_write\` to create all files. This is a React SPA that compiles in the browser and previews instantly.

---

## How the Runtime Works

- Write standard TypeScript/React code with normal imports
- NPM packages auto-convert to ESM: \`import X from 'lodash'\` just works
- All React hooks available globally (\`useState\`, \`useEffect\`, etc.)
- CSS files auto-scoped
- **\`river.entity.query()\` returns an array directly** — not \`{ entities: [...] }\`

## Import Rules (CRITICAL — wrong imports cause "Element type is invalid: got object")

**Always \`export default\` for components, always use default imports:**
\`\`\`tsx
// ProductCard.tsx — correct
export default function ProductCard({ product }) { return <div>...</div>; }

// App.tsx — correct
import ProductCard from './components/ProductCard';  // ✅ default import

// ❌ NEVER do this — gives "got: object" error
import * as ProductCard from './components/ProductCard';
\`\`\`

**Never use \`@/\` path aliases — this is react-spa, not Next.js:**
\`\`\`tsx
import ProductCard from './components/ProductCard';  // ✅
import ProductCard from '@/components/ProductCard';  // ❌ only Next.js
\`\`\`

**Never use Next.js imports:**
\`\`\`tsx
import Link from 'next/link';   // ❌ not available
import Image from 'next/image'; // ❌ not available
// Use: <a href="..."> and <img src="..."> instead
\`\`\`

---

## Directory Structure

\`\`\`
src/
  App.tsx          # Root component
  App.css          # Global styles
  components/      # Reusable components
  hooks/           # Custom hooks
  utils/           # Helpers
\`\`\`

---

## Accessing River Data

\`\`\`typescript
// Query entities — returns array directly!
const items = await river.entity.query({
  entityType: 'Product',  // or any entity type
  limit: 50
});
items.map(item => item.name);  // ✅ works — items IS the array

// Create an entity
const newEntity = await river.entity.create({
  entityType: 'Task',
  name: 'My task',
  skeleton: { status: 'pending', priority: 'high' }
});

// Update an entity
await river.entity.update(entityId, {
  skeleton: { status: 'done' }
});

// Read full content of a Doc or Sheet (query only returns metadata)
const { markdown } = await river.entity.readContent({ entityId: doc._id });
const { csv, headers } = await river.entity.readContent({ entityId: sheet._id });

// Call any River tool (workspace only — returns { status: 'error' } in deployed apps)
const result = await river.tools.execute('canvas_write', { entityId: 'canvas_abc', ... });
if (result.status === 'success') { ... }
\`\`\`

---

## Starter Skeleton

\`\`\`typescript
// src/App.tsx
import { useState, useEffect } from 'react';
import './App.css';

export default function App() {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    river.entity.query({ entityType: 'YourEntityType' })
      .then(results => {
        setData(results);
        setLoading(false);
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
  }, []);

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="app">
      <header>
        <h1>{{projectName}}</h1>
        <p>{{description}}</p>
      </header>
      <main>
        {/* Build the UI here */}
      </main>
    </div>
  );
}
\`\`\`

---

## Integration Access

If the app needs external data (bank transactions, email, etc.), first call the \`integration_list\` tool to see what's connected. If the required service isn't connected, call \`setup_integration\` to prompt the user to connect it.

---

## Styling

Use plain CSS or Tailwind (via CDN \`<link>\` in a root component). Match the visual style to the purpose: {{description}}.
`.trimStart();
